import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { listUsuariosPermisos } from "../api/usuarios";

const PermisosContext = createContext(null);

export const PermisosProvider = ({ children }) => {
    const [permisos, setPermisos] = useState(null); // Objeto completo de la respuesta
    const [modulosPermitidos, setModulosPermitidos] = useState([]); // Array de nombres de módulos
    const [loadingPermisos, setLoadingPermisos] = useState(false);

    /**
     * Carga los permisos del usuario autenticado y los almacena en el contexto.
     * Se llama desde Login.jsx después de un login exitoso.
     */
    const cargarPermisos = useCallback(async () => {
        setLoadingPermisos(true);
        try {
            const res = await listUsuariosPermisos();
            if (res && res.data) {
                setPermisos(res.data);
                // Extraer la lista de nombres de módulos que el usuario tiene asignados
                const modulos = (res.data?.Permiso?.PermisosModulos || []).map(
                    (pm) => pm.Modulo?.modulo?.toLowerCase()
                ).filter(Boolean);
                setModulosPermitidos(modulos);
            }
        } catch (err) {
            console.error("Error cargando permisos:", err);
        } finally {
            setLoadingPermisos(false);
        }
    }, []);
    
    /**
     * Efecto para cargar los permisos automáticamente al montar o refrescar
     * si el token existe pero no hay permisos cargados aún.
     */
    useEffect(() => {
        if (sessionStorage.getItem("token") && !permisos) {
            cargarPermisos();
        }
    }, [cargarPermisos, permisos]);

    /**
     * Limpia los permisos al cerrar sesión.
     */
    const limpiarPermisos = useCallback(() => {
        setPermisos(null);
        setModulosPermitidos([]);
    }, []);

    /**
     * Verifica si el usuario tiene acceso a un módulo por nombre (case-insensitive).
     * @param {string} nombreModulo - Nombre del módulo a verificar
     */
    const tienePermiso = useCallback((nombreModulo) => {
        if (!modulosPermitidos.length) return false;
        return modulosPermitidos.includes(nombreModulo.toLowerCase());
    }, [modulosPermitidos]);

    /**
     * Obtiene el objeto PermisosModulos de un módulo específico.
     * @param {string} nombreModulo
     */
    const getPermisoModulo = useCallback((nombreModulo) => {
        const pms = permisos?.Permiso?.PermisosModulos || [];
        return pms.find(
            (pm) => pm.Modulo?.modulo?.toLowerCase() === nombreModulo.toLowerCase()
        );
    }, [permisos]);

    return (
        <PermisosContext.Provider value={{
            permisos,
            modulosPermitidos,
            loadingPermisos,
            cargarPermisos,
            limpiarPermisos,
            tienePermiso,
            getPermisoModulo,
        }}>
            {children}
        </PermisosContext.Provider>
    );
};

export const usePermisos = () => {
    const ctx = useContext(PermisosContext);
    if (!ctx) throw new Error("usePermisos debe usarse dentro de PermisosProvider");
    return ctx;
};

export default PermisosContext;
